import React, { isValidElement, useId, useState } from 'react';
import { IconButton } from '../controls/controls.jsx';
import { Avatar } from '../identity/Avatar.jsx';
import { Tooltip } from '../overlays/Tooltip.jsx';
import { Badge, SectionHeader } from '../display/display.jsx';
import { IconChevronDown, IconChevronUp, IconFileText, IconFlag } from '../icons.js';
import './records.css';

const classes = (...values) => values.filter(Boolean).join(' ');

/** One scroll owner contains the native dialog heading and every record section. */
export function RecordDetailScrollLayout({children,className='',...props}) {
  return <div className={classes('bs-record-detail-scroll',className)} data-record-scroll {...props}>{children}</div>;
}

/** The same heading shell serves a page, a dialog title and status-only record controls. */
export function RecordHeader({ title, description, status, meta, actions, titleAs: Title = 'h2', descriptionAs: Description = 'p', titleClassName = '', descriptionClassName = '', className = '', ...props }) {
  const hasTitle = title != null && title !== '';
  return <header className={classes('bs-record-header', className)} data-has-title={hasTitle ? 'true' : 'false'} {...props}>
    <div className="bs-record-heading-copy">
      {status && <div className="bs-record-status">{status}</div>}
      {hasTitle && <Title className={classes('bs-record-title', titleClassName)}>{title}</Title>}
      {description != null && description !== '' && <Description className={classes('bs-record-description', descriptionClassName)}>{description}</Description>}
      {meta != null && <div className="bs-record-meta">{meta}</div>}
    </div>
    {actions && <div className="bs-record-header-actions">{actions}</div>}
  </header>;
}

/** `audience="brand"` never shows a person or an internal assignment state; `owner={false}` drops the owner row. */
export function NextStepCard({ title = 'Sıradaki adım', nextAction, owner, waiting, action, children, audience = 'team', className = '', ...props }) {
  const brand = audience === 'brand';
  const ownerText = owner === false ? null : brand ? 'BiSponsor ekibi' : owner || 'Atama bekliyor';
  return <section className={classes('bs-next-step-card', className)} aria-label={title} {...props}>
    <Badge tone="warning" className="bs-next-step-label"><IconFlag size={15} aria-hidden="true" />{title}</Badge>
    <h3 className="bs-next-step-title">{nextAction || (brand ? 'Ekibimiz talebinizi inceliyor' : 'Henüz belirlenmedi')}</h3>
    {children && <div className="bs-next-step-description">{children}</div>}
    {(ownerText || waiting) && <dl className="bs-next-step-meta">
      {ownerText && <div><dt>Sorumlu</dt><dd>{ownerText}</dd></div>}
      {waiting && <div><dt>Beklenen</dt><dd>{waiting}</dd></div>}
    </dl>}
    {action && <div className="bs-next-step-actions">{action}</div>}
  </section>;
}

export function OutcomeCard({ title = 'İşin sonucu', summary, className = '' }) {
  return <section className={classes('bs-outcome-card', className)} aria-label={title}><h3>{title}</h3><p>{summary || 'İş kapatıldı.'}</p></section>;
}

export function LinkedRecord({ children, onClick }) {
  return onClick ? <button type="button" className="bs-linked-record" onClick={onClick}>{children}</button> : <span>{children}</span>;
}

export function DetailCard({ title, action, collapsible = false, defaultOpen = true, open: controlledOpen, onOpenChange, children, className = '', ...props }) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const id = useId();
  const open = controlledOpen === undefined ? localOpen : controlledOpen;
  const expanded = !collapsible || open;
  const toggle = () => { const next = !open; if (controlledOpen === undefined) setLocalOpen(next); onOpenChange?.(next); };
  const collapseLabel = `${title} ayrıntılarını ${open ? 'gizle' : 'göster'}`;
  const actions = (action || collapsible) && <div className="bs-detail-card-actions">{action}{collapsible && <Tooltip content={collapseLabel}><IconButton label={collapseLabel} aria-expanded={open} aria-controls={id} onClick={toggle}>{open ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}</IconButton></Tooltip>}</div>;
  return <section className={classes('bs-detail-card', className)} aria-label={title} data-expanded={expanded ? 'true' : 'false'} {...props}>
    <SectionHeader className="bs-detail-card-heading" title={title} action={actions} />
    <div id={id} className="bs-detail-card-body" hidden={!expanded}>{children}</div>
  </section>;
}

export function MetaRow({ label, value, children, className = '', ...props }) {
  return <div className={classes('bs-meta-row', className)} {...props}><dt>{label}</dt><dd>{value ?? children ?? '—'}</dd></div>;
}

function ActivityIcon({ icon }) {
  if (!icon) return <IconFileText size={17} />;
  return isValidElement(icon) ? icon : React.createElement(icon, { size: 17 });
}

/** Items are already authorized and ordered by the feature adapter. */
export function ActivityTimeline({ items = [], label = 'İşlem geçmişi', emptyMessage = 'Henüz işlem kaydı yok.', className = '', ...props }) {
  if (!items.length) return <p className={classes('bs-activity-empty', className)}>{emptyMessage}</p>;
  return <ol className={classes('bs-activity-timeline', className)} aria-label={label} {...props}>
    {items.map((item, index) => <li className="bs-activity-item" key={item.id ?? index}>
      <span className="bs-activity-marker" data-kind={item.kind || 'event'} aria-hidden="true"><ActivityIcon icon={item.icon} /></span>
      <div className="bs-activity-content">
        <div className="bs-activity-heading"><p className="bs-activity-body">{item.body}</p>{item.date && <time dateTime={item.dateTime}>{item.date}</time>}</div>
        {(item.actor || item.internal) && <div className="bs-activity-meta">{item.actor && <><Avatar name={item.actor} label={null} size={20}/><span className="bs-activity-actor">{item.actor}</span></>}{item.internal && <span className="bs-activity-internal">Yalnız ekip</span>}</div>}
      </div>
    </li>)}
  </ol>;
}
